import { useEffect, useState } from 'react';
import { Button, Caption, LargeTitle, Title } from '@telegram-apps/telegram-ui';
import { BoxCodeView } from '@veylme/shared';

import { apiFetch, emitEvent } from '../api/client.js';
import { hapticNotification, shareToStoryOrUnsupported } from '../lib/tg.js';
import { useT } from '../hooks/useT.js';
import styles from './CodeShareModal.module.css';

/**
 * CodeShareModal — нижняя шторка кода для не-Premium автора (work-020, решение 1/2).
 *
 * Не-Premium в сторис не получает кликабельную ссылку (Premium-only). Вместо неё
 * показываем короткий код: автор копирует его / делится сторис, а зритель вводит
 * код в приложении (AskByCodeScreen) и задаёт анонимный вопрос.
 *
 * Своя вёрстка (НЕ нативный `showPopup`: нужен крупный код + «копировать» +
 * «поделиться»), паттерн нижней шторки тот же, что у `AgeGateSheet`
 * (backdrop + прижатый к низу sheet, верхние углы radius `sheet`=16, DESIGN §5).
 *
 * Жизненный цикл: на open → `POST /boxes/code` (ленивая идемпотентная выдача
 * своего кода). Состояния loading → показ кода | error (+ «Закрыть»).
 *
 * CANON G2/I4: shareToStory вызывается ТОЛЬКО из onClick share-кнопки.
 * Инвариант (work-020 находка #5): `emitEvent(invite_shared_to_story)` шлём
 * ТОЛЬКО на реальный результат 'shared', а НЕ на открытие модалки (open ≠ share).
 */
export interface CodeShareModalProps {
  /** Показывать ли шторку. Не рисуется и не грузит код, если false. */
  open: boolean;
  /** Закрыть шторку (крестик-аналог «Закрыть» / после успешного share). */
  onClose: () => void;
  /** Медиа-карточка приглашения для shareToStory (`box.inviteCardUrl`). */
  inviteCardUrl: string;
}

type CodeState =
  | { kind: 'loading' }
  | { kind: 'ready'; code: string; inviteCardUrl: string | null }
  | { kind: 'error' };

export function CodeShareModal({ open, onClose, inviteCardUrl }: CodeShareModalProps) {
  const t = useT();
  const [state, setState] = useState<CodeState>({ kind: 'loading' });
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  // Инлайн-уведомление о результате шеринга (unsupported/failed) — не падаем.
  const [shareNotice, setShareNotice] = useState<string | null>(null);

  // На каждое открытие: ленивая идемпотентная выдача кода (POST /boxes/code).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    setShareNotice(null);
    setCopyStatus('idle');
    (async () => {
      try {
        const view = await apiFetch('/boxes/code', {
          method: 'POST',
          body: {},
          parse: BoxCodeView,
        });
        if (!cancelled) setState({ kind: 'ready', code: view.code, inviteCardUrl: view.inviteCardUrl });
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  async function handleCopy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 1500);
    } catch {
      setCopyStatus('failed');
      setTimeout(() => setCopyStatus('idle'), 1500);
    }
  }

  function handleShare() {
    setShareNotice(null);
    // work-021: если state.kind==='ready', берём inviteCardUrl из state (карточка с кодом).
    // Fallback на props.inviteCardUrl: degraded share без кода при render-failure (null-safe).
    const cardUrl =
      state.kind === 'ready'
        ? (state.inviteCardUrl ?? inviteCardUrl)
        : inviteCardUrl;
    // widgetLinkUrl ПУСТОЙ → у не-Premium кликабельной ссылки нет, апселла нет
    // (CANON A2/F1: бренд вшит в карту).
    const result = shareToStoryOrUnsupported(cardUrl, {
      text: t.tagline,
      widgetLinkUrl: '',
      widgetLinkName: t.share_ask_action,
    });
    if (result === 'shared') {
      hapticNotification('success');
      // Инвариант (находка #5): событие шлём ТОЛЬКО на реальный 'shared'.
      void emitEvent({ type: 'invite_shared_to_story', channel: 'story' });
      onClose();
    } else if (result === 'unsupported') {
      setShareNotice(t.inbox_story_unsupported);
    } else {
      setShareNotice(t.inbox_story_error);
    }
  }

  return (
    <div role="dialog" aria-modal="true" className={styles.backdrop}>
      <div className={styles.sheet}>
        {state.kind === 'loading' && (
          <Caption level="1" className={styles.statusText}>
            {t.code_modal_loading}
          </Caption>
        )}

        {state.kind === 'error' && (
          <>
            <Caption level="1" className={styles.statusText}>
              {t.code_modal_error}
            </Caption>
            <div className={styles.btns}>
              <Button size="l" mode="plain" stretched onClick={onClose}>
                {t.cancel}
              </Button>
            </div>
          </>
        )}

        {state.kind === 'ready' && (
          <>
            <Title level="3" weight="2" className={styles.title}>
              {t.code_modal_title}
            </Title>
            <Caption level="1" className={styles.explain}>
              {t.code_modal_explain}
            </Caption>

            <Caption level="1" caps className={styles.codeLabel}>
              {t.code_modal_your_code}
            </Caption>
            <LargeTitle weight="1" className={styles.code}>
              {state.code}
            </LargeTitle>

            <div className={styles.btns}>
              <Button
                size="l"
                mode="bezeled"
                stretched
                onClick={() => handleCopy(state.code)}
              >
                {copyStatus === 'copied' ? t.code_modal_copied : t.code_modal_copy}
              </Button>
              <Button size="l" stretched onClick={handleShare}>
                {t.code_modal_share_btn}
              </Button>
              {shareNotice !== null && (
                <Caption
                  Component="p"
                  className={styles.shareNotice}
                >
                  {shareNotice}
                </Caption>
              )}
              <Button size="l" mode="plain" stretched onClick={onClose}>
                {t.cancel}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
